import React, { useState, useEffect, useRef } from 'react';
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import { EuiLoadingSpinner, EuiText, EuiSpacer } from '@elastic/eui';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';
import { ClusterInStageDetail } from './ClusterInStageDetail';
import { StatusPieChart } from './components/StatusPieChart';
import { StageBarChart } from './components/StageBarChart';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';

export const StageDetail = ({ releaseId, stackName }) => {
  const [isRequestActive, setIsRequestActive] = useState(true);
  const [stages, setStages] = useState([]);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(['Success', 'Failed', 'In Progress']);
  const tableRef = useRef();

  const { data, error: hookError } = useSdpdsReadOnlyOperation({
    operation_name: "ListStagesInRelease",
    payload: {
      releaseId,
      stackName,
      maxResults: 100,
      nextToken: nextToken
    }
  });

  const updateResponse = (response) => {
    if (response) {
      const newStages = response.stages || [];
      setStages(prevStages => [...prevStages, ...newStages]);

      if (response.nextToken) {
        setNextToken(response.nextToken);
      } else {
        setIsRequestActive(false);
      }
    }
  };

  useEffect(() => {
    if (hookError) {
      setError(hookError.message || 'An error occurred while fetching stage details');
      setIsRequestActive(false);
    } else if (data?.sdpdsReadOnlyOperation) {
      updateResponse(data.sdpdsReadOnlyOperation);
    }
  }, [data, hookError]);

  useEffect(() => {
    setStages([]);
    setNextToken(null);
    setIsRequestActive(true);
    setError(null);
  }, [releaseId, stackName]);

  const scrollToClusterInStage = () => {
    if (tableRef.current) {
      const yOffset = -20;
      const element = tableRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const calculateTotals = () => {
    if (!stages?.length) return { scheduled: 0, success: 0, failed: 0, inProgress: 0 };

    return stages.reduce(
      (acc, stage) => {
        const inProgress = stage.deployMetrics.nScheduled -
          (stage.deployMetrics.nSuccess + stage.deployMetrics.nFailed);
        return {
          scheduled: acc.scheduled + stage.deployMetrics.nScheduled,
          success: acc.success + stage.deployMetrics.nSuccess,
          failed: acc.failed + stage.deployMetrics.nFailed,
          inProgress: acc.inProgress + inProgress
        };
      },
      { scheduled: 0, success: 0, failed: 0, inProgress: 0 }
    );
  };

  const parseStageNumber = (identifier) => {
    if (!identifier) return null;
    const match = String(identifier).match(/Stage\s+(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    if (value === 'all') {
      setSelectedFilters(['Success', 'Failed', 'In Progress']);
    } else {
      setSelectedFilters(value.split(','));
    }
  };

  const handleStageSelect = (stageIdentifier) => {
    const stageNumber = parseStageNumber(stageIdentifier);
    if (stageNumber !== null) {
      setSelectedStage(stageNumber);
      setTimeout(scrollToClusterInStage, 100);
    } else {
      console.warn('Invalid stage identifier:', stageIdentifier);
    }
  };

    if (isRequestActive) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <EuiLoadingSpinner size="xl" />
        </div>
      );
    }

    if (error) {
      return (
        <EuiText color="danger" style={{ padding: '1rem' }}>
          <p>{error}</p>
        </EuiText>
      );
    }

    if (!stages?.length) {
      return (
        <EuiText style={{ padding: '1rem' }}>
          <p>No stage data available.</p>
        </EuiText>
      );
    }

    const stageData = { stages };

    const totals = calculateTotals();

    return (
      <ElementErrorBoundary>
        <Container>
          <SpaceBetween size="l">
            <Header
              variant="h2"
              description={`Release ID: ${releaseId}`}
            >
              Stage Details
            </Header>

            <ColumnLayout columns={4} variant="text-grid">
              <Box variant="awsui-key-label">
                Total Scheduled
                <div>{totals.scheduled}</div>
              </Box>
              <Box variant="awsui-key-label">
                Total Success
                <div>{totals.success}</div>
              </Box>
              <Box variant="awsui-key-label">
                Total Failed
                <div>{totals.failed}</div>
              </Box>
              <Box variant="awsui-key-label">
                Success Rate
                <div>
                  {((totals.success / totals.scheduled) * 100).toFixed(1)}%
                </div>
              </Box>
            </ColumnLayout>

            <ColumnLayout columns={2}>
              <StatusPieChart stageData={stageData} />
              <StageBarChart
                stageData={stageData}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                onStageSelect={handleStageSelect}
              />
            </ColumnLayout>
          </SpaceBetween>
          {(selectedStage === 0 || selectedStage) && (
            <>
              <EuiSpacer size="l" />
              <div ref={tableRef}>
                <ClusterInStageDetail
                  releaseId={releaseId}
                  stackName={stackName}
                  stageId={selectedStage}
                />
              </div>
            </>
          )}
        </Container>
      </ElementErrorBoundary>
    );
};
